import { FC, useContext, useEffect } from 'react';
import { Outlet } from 'react-router';
import { useParams, useLocation } from 'react-router-dom';

import ErrorBoundary from 'modules/auth/pages/ErrorBoundary/ErrorBoundary';
import { getText } from 'utils/stringUtil';
import {
  BENEFITS_GUIDE_UNDERGOING_UPDATES,
  CHECK_BACK_LATER_CONTACT_ADMIN,
  BENEFITS_GUIDE_NO_LONGER_AVAILABLE,
  CONTACT_SYSTEM_ADMINISTRATOR,
  NO_ACCESS_CONTACT_BROKER,
  BENEFIT_GUIDE_NO_LONGER_AVAILABLE,
} from 'constants/languageConstants';
import AppLayout from 'layout/AppLayout';
import {
  authenticateCompleted,
  initBenguideDataByHash,
  initBenguideDataByToken,
} from 'modules/auth/slices/authSlice';
import { initBenGuide } from 'modules/auth/slices/benguideSlice';
import { useAppSelector, useAppDispatch } from 'hooks/redux';
import { NavigationContext } from 'context/NavigationContext';
import {
  ADDITIONAL_CONTENT_OPTIONS,
  BENEFIT_TYPES,
} from 'modules/home/constants';
import ContentPage from 'model/CotentPage';
import useBenguide from 'hooks/benguide';
import { useMicrositeLivenessWatch } from 'hooks/useMicrositeLivenessWatch';
import Login from 'modules/auth/pages/Login/Login';
import FullScreenLoader from 'components/FullScreenLoader/FullScreenLoader';
import { isInIframe, http as axios } from 'utils/httpUtil';
import { getBenConsultation } from 'modules/benefitsWellness/slices/benefitsConsultationSlice';

type PrivateRouteProps = {
  layout?: any;
};

const PrivateRoute: FC<PrivateRouteProps> = (props: PrivateRouteProps) => {
  const { auth } = useAppSelector((state) => state);
  const benGuideLoading = useAppSelector((state) => state.benguide.inProgress);
  const benefitGuide = useAppSelector(
    (state) => state.basicInfo.layoutBenefitGuide
  );
  const language = useAppSelector((state) => state.benguide.data?.language);
  const dispatch = useAppDispatch();
  const { hash = '', employerName = '' } = useParams();
  const search = useLocation().search;
  const previewToken = new URLSearchParams(search).get('previewToken');

  const { setDisabledRoutes } = useContext(NavigationContext);
  const benguide = useBenguide();
  const { inProgress, benguideError } = auth;
  const { authFailed, notPublishedBenguide, notFound, invalidated } =
    benguideError || {};

  useEffect(() => {
    axios.defaults.withCredentials = true;
    if (!benguide.id) {
      if (previewToken) {
        dispatch(initBenguideDataByToken(previewToken, hash));
      } else {
        dispatch(initBenguideDataByHash(hash, isInIframe()));
      }
    }
  }, [dispatch, hash, previewToken, benguide.id]);

  useEffect(() => {
    benguide.masterId && dispatch(getBenConsultation(benguide.masterId));
  }, [benguide.masterId, dispatch]);

  // `authFailed` is the PARENT flag `authenticateFailed` sets for every
  // error path, notPublishedBenguide/notFound/invalidated included — it is
  // not a distinct fourth state. The only case it means on its own (none of
  // the other three set) is "no specific reason, show the login form",
  // which is what actually needs to stay terminal here.
  const bareAuthFailure =
    authFailed && !notPublishedBenguide && !notFound && !invalidated;

  // UAT-3254 — a shared microsite link never re-checks itself after the
  // initial load. Only watches the plain hash flow: a preview link
  // (`previewToken`) is the broker looking at their own in-progress work,
  // not a shared link that can go stale out from under a reader.
  //
  // UAT-3260 — deliberately kept enabled THROUGH `notPublishedBenguide`: a
  // guide moved to draft/in-progress/archived can just as easily be
  // published again while the same reader still has this exact screen
  // open, and nothing noticed that either — same bug, opposite direction.
  //
  // UAT-3290 — same reasoning extended to `bareAuthFailure` (the login
  // form): removing a link's password is exactly as much a live-editable
  // setting as its publish status, and a guest sitting on the login screen
  // deserves the same automatic recovery a guest sitting on "undergoing
  // updates" already gets, not a dead end until they happen to refresh.
  // `attemptPublicAuthentication`'s existing retry-without-password on a
  // 401/403 already succeeds transparently once no password is required —
  // this hook just has to be allowed to reach that call from here, and
  // `authenticateFailed`'s reducer is idempotent on a repeat 401/403 (see
  // its own classification logic), so polling through an unchanged
  // password requirement is a harmless no-op each tick.
  //
  // Gated on `!inProgress` instead of `!!benguide.id`, which a
  // password-protected guide's very first load never sets (it fails before
  // ever reaching `initBenGuide`) — `inProgress` is what actually tracks
  // whether the initial exchange has settled, success or failure alike.
  // `notFound`/`invalidated` remain the only two genuinely terminal states:
  // those mean the link itself is gone or the session lapsed, not
  // "temporarily denied", so there is no recovery to watch for.
  useMicrositeLivenessWatch(
    hash,
    !previewToken && !inProgress && !notFound && !invalidated,
    (data) => {
      // Only acts while a screen this poll can recover FROM is actually
      // showing — an ordinary poll on an already-healthy guide must stay a
      // no-op, or every successful tick would re-run the full init dispatch
      // for nothing.
      if (notPublishedBenguide || bareAuthFailure) {
        dispatch(authenticateCompleted());
        dispatch(initBenGuide(data));
      }
    }
  );

  // checks benefit guide object for disabled sections and disables related route
  useEffect(() => {
    if (benefitGuide) {
      const disabledBenefitRoutes = Object.entries(BENEFIT_TYPES)
        .filter(([key]) => {
          const contentPage: ContentPage = (benefitGuide as any)[key];
          return contentPage && !contentPage.enabled;
        })
        .map(([, value]) => {
          return value.route;
        });
      const disabledContentRoutes = Object.entries(ADDITIONAL_CONTENT_OPTIONS)
        .filter(([key]) => {
          const contentPage: ContentPage = (benefitGuide as any)[key];
          return contentPage && !contentPage.enabled;
        })
        .map(([, value]) => {
          return value.route;
        });
      setDisabledRoutes([...disabledBenefitRoutes, ...disabledContentRoutes]);
    }
  }, [benefitGuide, setDisabledRoutes]);

  // UAT-3257 — once the password screen is up, a retry stays on it.
  //
  // `inProgress` goes true the instant a password is submitted, which used to
  // swap `Login` out for this loader and back again when the attempt failed —
  // remounting it, so the typed password vanished and the screen visibly
  // flashed. That flash is the "the page reloads and reroutes us to the login
  // screen" in the ticket. `Login` has its own in-flight state on its button,
  // so it does not need the whole screen taken away to say it is working.
  if (!authFailed && (inProgress || benGuideLoading || !benguide.id)) {
    return <FullScreenLoader />;
  }

  if (notPublishedBenguide) {
    return (
      <ErrorBoundary
        lineOne={getText(BENEFITS_GUIDE_UNDERGOING_UPDATES, language)}
        lineTwo={getText(CHECK_BACK_LATER_CONTACT_ADMIN, language)}
      />
    );
  }

  if (notFound) {
    return (
      <ErrorBoundary
        lineOne={getText(BENEFITS_GUIDE_NO_LONGER_AVAILABLE, language)}
        lineTwo={getText(CONTACT_SYSTEM_ADMINISTRATOR, language)}
      />
    );
  }

  if (invalidated) {
    return (
      <ErrorBoundary lineOne={getText(NO_ACCESS_CONTACT_BROKER, language)} />
    );
  }

  if (authFailed) {
    return <Login />;
  }

  if (
    benefitGuide?.status === 'PUBLISHED' &&
    employerName !== 'plan-recommender' &&
    ((benefitGuide?.employerName &&
      (employerName === '' || employerName !== benefitGuide.employerName)) ||
      (!benefitGuide?.employerName && employerName !== ''))
  ) {
    return (
      <ErrorBoundary
        lineOne={getText(BENEFIT_GUIDE_NO_LONGER_AVAILABLE, language)}
        lineTwo={getText(CONTACT_SYSTEM_ADMINISTRATOR, language)}
      />
    );
  }

  return (
    <AppLayout layout={props.layout}>
      <Outlet />
    </AppLayout>
  );
};

export default PrivateRoute;
